import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SchedulaPrivacy() {
  return (
    <PolicyLayout title="privacy policy" appName="Schedula" date="DECEMBER 2025">
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Schedula” (the “App”) handles
        information when you create time-slotted tasks, view your day or week
        agenda, and receive local reminders. Schedula is designed as a minimal,
        offline, personal planner with all core features running on your device
        and without a custom backend. This document covers onboarding, task
        creation and editing, local storage of tasks and preferences, scheduling
        of local notifications, and simple appearance and behavior settings.
        Because there is no account system and no cloud sync in the MVP, your
        content remains on your device unless you explicitly export or share it
        using tools outside the App. By using Schedula, you acknowledge this
        Policy and agree to its terms. If you do not agree, uninstall the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller that determines the purposes and means of processing
        for Schedula is Aleksandra Rakitina. For privacy questions or to
        exercise rights such as access, deletion, or correction related to any
        records we might control (for example, support correspondence), contact
        alexwadek@gmail.com. Because the App does not require registration and
        does not maintain a cloud account, identity verification typically
        involves confirming control of the email address used to contact us and
        providing minimal contextual details (device and app version). We retain
        privacy correspondence where permitted by law to document our responses
        and compliance.
      </p>

      <div className={s.title}>
        3) Key Principles (Local-First, Minimization, User Control)
      </div>
      <p>
        Local-first: task data and settings are stored on your device using
        app-scoped storage (e.g., SQLite or Hive). Minimization: the App
        collects only what is necessary to show your schedule and trigger local
        reminders; it does not send your tasks to our servers because we do not
        operate any. User control: you decide what to enter, how long to keep
        it, whether to enable notifications, and when to delete tasks or
        uninstall the App. These principles aim to reduce exposure and simplify
        compliance with platform privacy requirements.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Task Data” means items you create in the planner, including title,
        optional description, date, start time, end time, reminder preferences,
        and completion state. “Settings” means appearance and behavior options
        such as theme mode (Light/Dark/System), daily start and finish hours,
        and notification permission state. “Local Notifications” are
        device-level alerts scheduled by the App based on Task Data, delivered
        by the operating system at the configured time. “Processing” includes
        collecting, storing, displaying, updating, scheduling, and deleting
        these data elements on your device.
      </p>

      <div className={s.title}>5) What We Do Not Collect</div>
      <p>
        We do not collect or upload your Task Data, calendar contents, or
        Settings to our servers, because we do not run a backend for Schedula in
        the MVP. We do not ask for your name, email, phone number, contacts,
        precise location, or payment information inside the App. We do not use
        advertising SDKs, third-party analytics SDKs, or tracking pixels. We do
        not sell Personal Data and do not share your tasks with advertising
        networks.
      </p>

      <div className={s.title}>
        6) Categories of Data We Process (On-Device Only)
      </div>
      <p>
        The App processes and stores, on your device: (a) Task Data (title,
        optional description, date, start/end time, reminder toggle and lead
        time, completed state); (b) Settings (theme option, daily start and end
        hours, lightweight onboarding flags). When you enable a reminder, the
        App schedules a Local Notification with the task title and timing
        parameters; the operating system queues and displays it at the
        appropriate time. No remote copy is created by us.
      </p>

      <div className={s.title}>7) Sources of Data and Voluntary Provision</div>
      <p>
        All Task Data originates from you when you add or edit entries.
        Notification delivery requires your permission; without it, the App will
        not show alert banners or sounds. You may disable notifications at any
        time in system settings. The App may read the current system time,
        locale, and time-zone to render the day/week timeline correctly; these
        values are not uploaded to us.
      </p>

      <div className={s.title}>8) Onboarding and Permissions</div>
      <p>
        During onboarding the App may request permission to send notifications
        so that reminders can be delivered. Granting this permission is
        optional; you can still create and view tasks without reminders. If you
        decline, you may grant permission later in OS settings. The App does not
        request access to contacts, photos, camera, microphone, motion activity,
        or location in the MVP.
      </p>

      <div className={s.title}>9) Day View and Timeline Rendering</div>
      <p>
        The Day screen displays a continuous timeline (00:00–23:59) with your
        tasks arranged by start/end times and a live indicator of “now.”
        Rendering relies on Task Data stored locally and device clock
        information to place items correctly. This view updates as you make
        edits or mark tasks completed. No external service receives this
        information.
      </p>

      <div className={s.title}>10) Week View and Navigation</div>
      <p>
        The Week screen shows Monday–Sunday with swipe navigation between weeks
        and highlights the selected day. A compact list of that day’s tasks
        appears for quick context. All computations (which items fall on which
        day) occur locally using the device time-zone. There is no server query
        for this feature.
      </p>

      <div className={s.title}>
        11) Task Creation, Editing, and Deletion
      </div>
      <p>
        When you add a task, the App saves Task Data to local storage and, if
        the reminder toggle is enabled, schedules a Local Notification for
        “start time minus lead time” (for example, 10 minutes before). Editing a
        task updates the local record and reschedules notifications as needed.
        Deleting a task removes its local record and cancels any pending
        notifications associated with it. The App does not maintain a recycle
        bin; deletion is intended to be final for local storage, while OS-level
        backups (if you enabled them system-wide) are outside our control.
      </p>

      <div className={s.title}>
        12) Reminders and Local Notifications
      </div>
      <p>
        Reminders are implemented via a local notifications plugin that
        interacts with the OS scheduler. Each scheduled alert typically contains
        the task title and a short message. Delivery timing may vary due to
        device power management, Do Not Disturb, or user notification settings.
        Tapping a notification can open the App to the relevant day.
        Notifications are created and canceled locally; we do not route them
        through external servers.
      </p>

      <div className={s.title}>13) Settings and Appearance</div>
      <p>
        Settings such as theme mode (Light/Dark/System) and daily working hours
        (e.g., start 07:00, finish 23:00) are stored locally to customize your
        experience. Changing these options updates how the Day/Week views
        render. These preferences are not uploaded.
      </p>

      <div className={s.title}>14) Data Retention and Deletion</div>
      <p>
        Task Data and Settings remain on your device until you delete them or
        uninstall the App. Uninstalling removes the App’s sandbox and its local
        database from the device; if you re-install later, past data will not be
        restored unless your device OS restores an entire app backup at your
        direction. Because we do not host a backend for Schedula in the MVP, we
        cannot recover deleted local data.
      </p>

      <div className={s.title}>15) Security Measures (Local Context)</div>
      <p>
        We rely on platform sandboxing to isolate Schedula’s local database from
        other apps. Within the App, we use structured local storage (e.g.,
        SQLite/Hive) and avoid logging Task Data to system consoles.
        Communications with the OS to schedule notifications use platform APIs.
        There is no remote transmission of your tasks. While no system can be
        perfectly secure, limiting processing to your device reduces exposure to
        network risks.
      </p>

      <div className={s.title}>16) Children’s Privacy</div>
      <p>
        Schedula is a general-purpose personal planner and is not directed to
        children, and we do not knowingly collect Personal Data from children.
        The MVP does not provide account creation or social features. If you
        believe a minor has provided information to us via support email, contact
        alexwadek@gmail.com so we can delete such messages from our support
        mailbox.
      </p>

      <div className={s.title}>
        17) Third-Party Services and SDK Disclosures
      </div>
      <p>
        The MVP does not include cloud services, analytics SDKs, or advertising
        SDKs. A local notifications library interacts with the operating system
        to schedule alerts but does not transmit Task Data to external servers.
        If future versions add optional services (for example, cloud backup/sync
        or crash diagnostics), they will be off by default and accompanied by an
        updated Privacy Policy before activation.
      </p>

      <div className={s.title}>18) International Transfers</div>
      <p>
        Because Schedula does not upload Task Data in the MVP, international
        transfers of your schedule to our servers do not occur. If you email us
        for support, your email is processed by your and our email providers in
        accordance with their policies; such correspondence may involve
        international transfer depending on provider infrastructure.
      </p>

      <div className={s.title}>19) Your Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, or delete
        Personal Data we control. For Schedula’s MVP, nearly all content is
        stored only on your device. You can edit or delete tasks and settings
        directly in the App or by uninstalling it. If you email us, you may
        request deletion of your messages from our support mailbox after we
        verify identity. Notification permissions can be changed in device
        settings at any time.
      </p>

      <div className={s.title}>
        20) Do Not Track, Identifiers, and Cookies
      </div>
      <p>
        Schedula is a native app and does not use browser cookies. The App does
        not rely on advertising identifiers. Local storage uses a device
        database (e.g., SQLite/Hive) inside the app sandbox to store Task Data
        and Settings. No web beacons or cross-app trackers are embedded in the
        MVP.
      </p>

      <div className={s.title}>
        21) Platform Privacy Labels and Data Safety Disclosures
      </div>
      <p>
        For App Store and Google Play disclosures, Schedula’s MVP is designed to
        fit a “data not collected to developer servers” profile for Task Data
        and Settings, and “device-only” for notifications scheduling. If
        platform requirements change, we will align labels accordingly. Any
        future addition of cloud features will be reflected in those labels and
        in an updated Policy.
      </p>

      <div className={s.title}>
        22) Accuracy, Limitations, and Reliability
      </div>
      <p>
        The App provides a simple planner and reminder tool. Notification
        delivery can be affected by OS settings, battery optimization, Focus/Do
        Not Disturb, and device time changes. The timeline view assumes device
        clock accuracy; if your device time or time-zone is incorrect, task
        placement may not reflect actual local time. Always verify critical
        appointments with an authoritative source.
      </p>

      <div className={s.title}>23) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational or regulatory changes.
        The “Last Updated” date will change when updates occur. Material changes
        that affect your rights or our processing will be highlighted within the
        App (e.g., in Settings → About). Continued use after changes take effect
        signifies acceptance. If you disagree, you should stop using the App and
        remove it from your device.
      </p>

      <div className={s.title}>
        24) How to Contact Us and Verification Steps
      </div>
      <p>
        For privacy inquiries or requests, email alexwadek@gmail.com with a
        description of your question and your device/platform details. Because
        the App has no account system, we may ask you to reply from the same
        email thread or supply limited technical context to confirm the request
        is legitimate. We aim to respond within a reasonable time and will
        explain if a request cannot be fulfilled due to technical or legal
        limits.
      </p>
    </PolicyLayout>
  );
}
